import {
  BaseProgramTaskContent,
  MenuItemDescriptor,
  ProgramEngagementStatus,
  ProgramStepContent,
  ProgramTaskContent,
} from '@cibo/core'
import { ProgramBinding, ProgramTask } from '@cibo/core/src/types/Program/Binding'
import { ENGAGEMENT_ROUTES, EngagementRouteParams } from '@cibo/ui'
import Check from '@mui/icons-material/Check'
import HourglassTop from '@mui/icons-material/HourglassTop'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import {
  Collapse,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  alpha,
  styled,
  useTheme,
} from '@mui/material'
import dayjs from 'dayjs'
import { propEq } from 'ramda'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, generatePath } from 'react-router-dom'
import { useProgramEngagementContext } from '../../../context'
import { useProgramContent } from '../../../queries'
import { StepStatus } from './StepStatus'

const TaskButton = styled(ListItemButton)(() => {
  const theme = useTheme()
  return {
    '&.MuiListItemButton-root': {
      height: '50px',
      backgroundColor: theme.palette.background.default,
      '&.Mui-selected': {
        backgroundColor: alpha(theme.palette.secondary.main, 0.08),
      },
      '&:hover': {
        backgroundColor: alpha(theme.palette.secondary.main, 0.04),
      },
    },
  }
})

export const ProgramMenuItem = ({
  binding,
  stageId,
  stepId,
  taskId,
}: { binding: ProgramBinding[] } & EngagementRouteParams) => {
  const { basePath, engagement, program } = useProgramEngagementContext()
  const content = useProgramContent(program.data?.programId)
  const engagementId = engagement.data?.id

  const reviewingUnavailableStage = engagement.data?.getStepStatus(stageId)?.available === false

  if (!content.data || !program.data) {
    return null
  }

  return (
    <>
      {binding?.map(({ children, id, type }) => {
        const showChildren =
          type === 'stage' ||
          (type === 'step' && stepId === id && !!engagementId) ||
          (type === 'task' && taskId === id && !!engagementId)

        const engagementStatus = engagement.data?.getStatus({ id })
        const stepContent = program.data?.stepContent(id)?.content
        const taskContent = program.data?.taskContent(id)

        const disabled =
          !reviewingUnavailableStage && (!engagementStatus?.available || !engagementId)

        const menuItemDisplay = engagementStatus?.displays?.find(
          propEq('type', 'menuItem')
        ) as MenuItemDescriptor

        const titleFromConfig = menuItemDisplay?.params?.title
        const subTitleFromConfig = menuItemDisplay?.params?.subTitle
        const hideTaskInMenu = menuItemDisplay?.params?.hidden || taskContent?.hidden

        return (
          <Fragment key={id}>
            {type === 'step' ? (
              !!engagementStatus?.id ? (
                <StepMenuItem
                  basePath={basePath}
                  disabled={disabled}
                  engagementId={engagementId}
                  engagementStatus={engagementStatus}
                  id={id}
                  menuItemDisplay={menuItemDisplay}
                  reviewingUnavailableStage={reviewingUnavailableStage}
                  stageId={stageId}
                  stepContent={stepContent}
                  subTitleFromConfig={subTitleFromConfig}
                  titleFromConfig={titleFromConfig}
                />
              ) : null
            ) : type === 'task' && !hideTaskInMenu ? (
              <TaskMenuItem
                basePath={basePath}
                disabled={disabled}
                engagementId={engagementId}
                engagementStatus={engagementStatus}
                id={id}
                menuItemDisplay={menuItemDisplay}
                stageId={stageId}
                stepId={stepId}
                subTitleFromConfig={subTitleFromConfig}
                task={program.data?.task(id)}
                taskContent={taskContent}
                taskId={taskId}
                titleFromConfig={titleFromConfig}
              />
            ) : null}
            <Collapse in={!!showChildren}>
              {children.map(child => (
                <ProgramMenuItem
                  key={child.id}
                  binding={[child]}
                  stageId={stageId}
                  stepId={type === 'step' ? id : stepId}
                  taskId={taskId}
                />
              ))}
            </Collapse>
          </Fragment>
        )
      })}
    </>
  )
}

type MenuItemProps = {
  basePath?: string
  disabled?: boolean
  engagementId?: string
  engagementStatus?: ProgramEngagementStatus
  id?: string
  menuItemDisplay?: MenuItemDescriptor
  reviewingUnavailableStage?: boolean
  stageId?: string
  stepContent?: ProgramStepContent['content']
  stepId?: string
  subTitleFromConfig?: string
  task?: ProgramTask
  taskContent?: ProgramTaskContent<BaseProgramTaskContent>
  taskId?: string
  titleFromConfig?: string
}

const StepMenuItem = ({
  basePath,
  disabled,
  engagementId,
  engagementStatus,
  id,
  menuItemDisplay,
  reviewingUnavailableStage,
  stageId,
  stepContent,
  subTitleFromConfig,
  titleFromConfig,
}: MenuItemProps) => (
  <>
    <ListItem
      key={id}
      data-testid={`step-listitem-${id}`}
      divider
      secondaryAction={
        !!engagementId && (
          <StepStatus
            valid={engagementStatus?.status.progress?.completed}
            available={engagementStatus?.available}
            review={reviewingUnavailableStage}
          />
        )
      }
      disablePadding
    >
      <ListItemButton
        component={Link}
        to={
          disabled
            ? '#'
            : generatePath(`${basePath}${ENGAGEMENT_ROUTES.STEP}`, {
                engagementId,
                stepId: id,
                stageId,
              })
        }
        //@todo figure out why a ListItemButton has 0 height meaning we have these wack padding values
        sx={{ paddingBlock: 5, paddingInline: 2 }}
        disabled={disabled}
      >
        <ListItemText
          primary={titleFromConfig || stepContent?.title || engagementStatus?.id}
          secondary={menuItemDisplay ? subTitleFromConfig : stepContent?.subTitle}
        />
      </ListItemButton>
    </ListItem>
  </>
)

const TaskMenuItem = ({
  basePath,
  disabled,
  engagementId,
  engagementStatus,
  id,
  menuItemDisplay,
  stageId,
  stepId,
  subTitleFromConfig,
  task,
  taskContent,
  taskId,
  titleFromConfig,
}: MenuItemProps) => {
  const { displayAvailableAt } = taskStatus({ task, engagementStatus })

  return (
    <ListItem
      key={id}
      data-testid={`task-listitem-${id}`}
      secondaryAction={
        engagementStatus?.status.progress?.completed ? (
          <Typography
            color={
              !engagementStatus?.status.eligibility?.eligible ? 'warning.light' : 'success.light'
            }
          >
            {engagementStatus?.status.eligibility?.eligible ? (
              <TaskAltIcon data-testid="menu-item-eligible" fontSize="medium" />
            ) : (
              <Check data-testid="menu-item-ineligible" fontSize="medium" />
            )}
          </Typography>
        ) : !engagementStatus?.available && displayAvailableAt ? (
          <Typography color="text.disabled" paddingTop="6px">
            <HourglassTop fontSize="medium" opacity="0.4" />
          </Typography>
        ) : null
      }
      disablePadding
    >
      <TaskButton
        key={id}
        disabled={disabled}
        //@ts-ignore
        component={Link}
        to={
          disabled
            ? '#'
            : generatePath(`${basePath}${ENGAGEMENT_ROUTES.TASK}`, {
                engagementId: engagementId || '',
                stepId,
                taskId: id,
                stageId,
              })
        }
        selected={id === taskId}
        sx={{ paddingInline: 2, paddingBlock: 1.5 }}
        divider
      >
        <Stack direction="column">
          <Stack direction="row" spacing={1} alignItems="center">
            <ListItemText
              primary={titleFromConfig || taskContent?.content.title || engagementStatus?.id}
              secondary={menuItemDisplay ? subTitleFromConfig : taskContent?.content.subTitle}
            />
          </Stack>

          <DateRangeDisplay engagementStatus={engagementStatus} task={task} />
        </Stack>
      </TaskButton>
    </ListItem>
  )
}

const DateRangeDisplay = ({
  task,
  engagementStatus,
}: {
  task?: ProgramTask
  engagementStatus: any
}) => {
  const { t } = useTranslation('@cibo/impact/ProgramMenuItem')
  const { displayAvailableAt, displayAvailableUntil } = taskStatus({ task, engagementStatus })

  if (!task) {
    return null
  }

  return (
    task && (
      <Typography variant="caption">
        {task.availableAt && displayAvailableAt
          ? t('availableAt', { date: new Date(task.availableAt) })
          : task.availableUntil && displayAvailableUntil
          ? t('availableUntil', { date: new Date(task.availableUntil) })
          : null}
      </Typography>
    )
  )
}

const taskStatus = ({ task, engagementStatus }: { task?: ProgramTask; engagementStatus: any }) => {
  const isBeforeAvailableAt = task?.availableAt && dayjs().isBefore(dayjs(task.availableAt))
  return {
    isBeforeAvailableAt,
    displayAvailableAt:
      !engagementStatus?.available &&
      !engagementStatus?.status?.progress?.completed &&
      isBeforeAvailableAt,
    displayAvailableUntil:
      !(task?.availableUntil && dayjs().isAfter(dayjs(task.availableUntil))) &&
      !isBeforeAvailableAt,
  }
}
